import { AxiosError } from "axios";
import moment, { Moment } from "moment";

import ConstanceView from "issara-sdk/apis/ConstanceView_core";

import EmployeeListAPI from "issara-sdk/apis/EmployeeListAPI_users";

import ClockRecordList from "issara-sdk/apis/ClockRecordList_apps_HRM";
import LeaveRequestList from "issara-sdk/apis/LeaveRequestList_apps_HRM";
import PayrollRecordList from "issara-sdk/apis/PayrollRecordList_apps_HRM";
import WorkShiftAttendanceReportPipelineView from "issara-sdk/apis/WorkShiftAttendanceReportPipelineView_apps_HRM";
import WorkShiftDetail from "issara-sdk/apis/WorkShiftDetail_apps_HRM";
import WorkShiftHistoryList from "issara-sdk/apis/WorkShiftHistoryList_apps_HRM";
import WorkShiftList from "issara-sdk/apis/WorkShiftList_apps_HRM";
import WorkShiftScheduleReportPipelineView from "issara-sdk/apis/WorkShiftScheduleReportPipelineView_apps_HRM";
import WorkShiftSettingAutoApproveDetail from "issara-sdk/apis/WorkShiftSettingAutoApproveDetail_apps_HRM";
import WorkShiftSettingDetail from "issara-sdk/apis/WorkShiftSettingDetail_apps_HRM";
import WorkShiftSettingList from "issara-sdk/apis/WorkShiftSettingList_apps_HRM";
import WorkShiftTemplateList from "issara-sdk/apis/WorkShiftTemplateList_apps_HRM";

import ClockRecordSerializer from "issara-sdk/types/ClockRecordSerializer_apps_HRM";
import EmployeeSerializer from "issara-sdk/types/EmployeeSerializer_users";
import LeaveRequestSerializer from "issara-sdk/types/LeaveRequestSerializer_apps_HRM";
import PayrollRecordSerializer from "issara-sdk/types/PayrollRecordSerializer_apps_HRM";
import WorkShiftHistorySerializer from "issara-sdk/types/WorkShiftHistorySerializer_apps_HRM";
import WorkShiftSettingSerializer from "issara-sdk/types/WorkShiftSettingSerializer_apps_HRM";
import WorkShiftTemplateSerializer from "issara-sdk/types/WorkShiftTemplateSerializer_apps_HRM";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  combinePdfFiles,
  GenerateActionTypes,
  generatePrefixedActions,
  MasterMapData,
  MasterMapOptions,
  PickMainState,
  SetErrorMessage,
} from "react-lib/apps/HISV3/common/CommonInterface";

import FormEmployeeAttendanceReport from "../pdfForm/FormEmployeeAttendanceReport";
import FormWorkScheduleReport from "../pdfForm/FormWorkScheduleReport";

import { isTimeWithinShift } from "../HRMInterface";

/** ============== Type-Defining Constants =============  */
export const ACTIONS = {
  INIT: "INIT",
  ADD_EMPLOYEE: "ADD_EMPLOYEE",
  ALL_APPROVE: "ALL_APPROVE",
  APPLY_NEW_TIME: "APPLY_NEW_TIME",
  APPROVE_TIME: "APPROVE_TIME",
  CLOCK_RECORD: "CLOCK_RECORD",
  DUPLICATE: "DUPLICATE",
  EDIT_REGULAR_WORK: "EDIT_REGULAR_WORK",
  EMPLOYEE_ATTENDANCE_REPORT: "EMPLOYEE_ATTENDANCE_REPORT",
  OPEN_HISTORY: "OPEN_HISTORY",
  PRINT_WORK_SCHEDULE: "PRINT_WORK_SCHEDULE",
  REMOVE: "REMOVE",
  SAVE: "SAVE",
  SEARCH: "SEARCH",
} as const;

export const SEQUENCE_NAME = "SetupWorkSchedule" as const;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  SetupWorkScheduleSequence: Partial<{
    sequenceIndex: "Action" | "START" | null;
    abortCtrl?: AbortController;
    clockRecords: ClockRecordType[];
    constance: { HRM_OT_CUTOFF_DATE?: number };
    filter: FilterType;
    leaveRequests: Partial<Record<string, LeaveRequestType[]>>;
    payrollRecordEmployeeIds: Partial<Record<string, PayrollRecordEmployeeType[]>>;
    storeSlots: Partial<Record<string, StoreSlotType[]>>;
    workShiftHistory: WorkShiftHistoryType[] | null;
    workShifts: Partial<Record<string, WorkShiftType[]>>;
    workShiftSettings: WorkShiftSettingType[];
    workShiftTemplates: WorkShiftTemplateType[];
  }> | null;
  masterOptions: MasterOptions;
}>;

// STATUS_WORK = 'W'
// STATUS_OFF = 'O'
// STATUS_LEAVE = 'L'
// STATUS_ABSENT = 'A'
type ShiftStatusType = "L" | "O" | "W" | "X";

type ApproveStatusType = "A" | "P" | "R" | "X";

type ActionLogType = "Add" | "Deleted" | "Edited";

export type EventsTimeShiftType = Partial<
  Pick<
    WorkShiftTemplateType,
    "code" | "color_code" | "end_break_time" | "is_break_time" | "shift_type" | "start_break_time"
  >
> &
  TimeSheetType & {
    id?: number;
    actionLog?: ActionLogType;
    allDay?: boolean;
    approval_status?: ApproveStatusType;
    clock_in_datetime?: string;
    clock_out_datetime?: string;
    data?: Record<string, any>;
    deleted?: boolean;
    employee: number;
    end: Moment;
    has_history?: boolean;
    is_apply_new_time?: boolean;
    is_approve?: boolean;
    leave_end_time?: string;
    leave_option?: "A" | "AF" | "AH" | "F" | "H";
    leave_start_time?: string;
    parent?: number | null;
    resource?: any;
    shift_status: ShiftStatusType;
    start: Moment;
    template?: number;
    title: string;
    work_shift?:
      | (WorkShiftItemType & {
          employee: number;
          end: Moment;
          shift_status: ShiftStatusType;
          start: Moment;
          title: string;
        })[]
      | null;
  };

export type WorkShiftType = {
  id?: number;
  employee: number;
  employee_code: string;
  employee_full_name: string;
  events: EventsTimeShiftType[];
  logs: EventsTimeShiftType[];
  regular_work_hours: number | null;
};

export type WorkShiftHistoryType = {
  id: string;
  created: string;
  edit_user_full_name: string;
  shifts: {
    action: "Approve" | "Unapprove" | ActionLogType;
    created: string;
    end_break_time: string;
    end_time: string;
    is_break_time: boolean;
    name: string;
    start_break_time: string;
    start_time: string;
    total_hours: number;
  }[];
};

export type PayrollRecordEmployeeType = { date: Moment; employee: number };

export type DuplicateEmployeeType = {
  type: "EMPLOYEE_ONLY" | "SHIFT_TIME";
  viewMonth: Moment;
  yearMonth?: string;
};

export type ClockRecordParamsType = {
  employee: number;
  end: Moment;
  isClear?: boolean;
  shift_type?: WorkShiftTemplateSerializer["shift_type"];
  start: Moment;
};

export type ApplyNewTimeParamsType = {
  data: ShiftTimeWorkType;
  event: EventsTimeShiftType;
  index: number;
  onError: () => void;
  onSuccess: () => void;
};

export type EditRegularWorkParams = {
  data: WorkShiftType;
  value: number | null;
  viewMonth?: Moment;
};

export type ApproveTimeParamsType = Omit<ApplyNewTimeParamsType, "data"> & {
  data: TimeSheetType;
  isFullDayLeave: boolean;
  isScheduledOff: boolean;
};

type AddEmployeeParamsType = {
  departmentId: number | null;
  employeeData: Record<string, any> | null;
  viewMonth: Moment;
};

export type OpenHistoryParamsType = {
  date: Moment;
  employee: number;
};

export type TimeSheetType = Partial<{
  clock_in: number | null;
  clock_out: number | null;
  OT_clock_in_1_5hr: string | null;
  OT_clock_in_1hr: string | null;
  OT_clock_in_3hr: string | null;
  OT_clock_out_1_5hr: string | null;
  OT_clock_out_1hr: string | null;
  OT_clock_out_3hr: string | null;
}>;

export type ActionSaveType = "APPLY_BLANK_OFF" | "NO_BLANK_OFF";

export type HolidaysType = {
  active?: boolean;
  allDay?: boolean;
  day?: number;
  end: Moment;
  name?: string;
  resource?: any;
  start: Moment;
  title: string;
};

export type ShiftType = Pick<
  EventsTimeShiftType,
  | "end"
  | "end_break_time"
  | "is_break_time"
  | "leave_end_time"
  | "leave_start_time"
  | "OT_clock_in_1_5hr"
  | "OT_clock_in_1hr"
  | "OT_clock_in_3hr"
  | "OT_clock_out_1_5hr"
  | "OT_clock_out_1hr"
  | "OT_clock_out_3hr"
  | "start"
  | "start_break_time"
>;

export type StoreSlotType = {
  action: "ADD" | "DELETE" | "DUPLICATE" | "OFF";
  data?: Record<string, any>;
  event?: EventsTimeShiftType;
  events?: EventsTimeShiftType[];
  steps?: number;
  timeShifts?: WorkShiftType[];
  workShiftSettings?: WorkShiftSettingType[];
};

export type WorkShiftTemplateType = Omit<WorkShiftTemplateSerializer, "code" | "shift_type"> & {
  code?: string;
  shift_type?: "" | "D" | "N";
  template_shift_type?: WorkShiftTemplateSerializer["shift_type"];
};

export type WorkShiftSettingType = Omit<WorkShiftSettingSerializer, "employees" | "holiday"> & {
  employees: {
    id?: number;
    deleted?: boolean;
    employee: number;
    employee_code: string;
    employee_full_name?: string;
    regular_work_hours?: number | null;
    work_shift_setting?: number;
  }[];
  holiday: HolidaysType[];
  // employee รก ที่ต้องการลบ
  removes?: { id?: number; employee: number }[];
};

type WorkShiftResponseType = Pick<
  EventsTimeShiftType,
  "approval_status" | "clock_in_datetime" | "clock_out_datetime"
> & {
  id: number;
  clock_in: number | null;
  clock_out: number | null;
  day: number;
  deleted: boolean;
  employee_code: string;
  employee_full_name: string;
  employee_id: string;
  leave_end_time?: string;
  leave_start_time?: string;
  manual_template_color_code?: string;
  manual_template_end_break_time?: string | null;
  manual_template_end_time?: string;
  manual_template_is_break_time?: boolean;
  manual_template_shift_type?: WorkShiftTemplateSerializer["shift_type"];
  manual_template_start_break_time?: string | null;
  manual_template_start_time?: string;
  OT_clock_in_1_5hr: string | null;
  OT_clock_in_1hr: string | null;
  OT_clock_in_3hr: string | null;
  OT_clock_out_1_5hr: string | null;
  OT_clock_out_1hr: string | null;
  OT_clock_out_3hr: string | null;
  parent?: number | null;
  shift_status: "O" | "W" | "X";
  template_detail: WorkShiftTemplateType | null;
};

type WorkShiftHistoryResponseType = WorkShiftHistorySerializer & {
  log_detail: { action: ActionLogType; note?: string };
};

export type WorkShiftItemType = Pick<
  WorkShiftTemplateType,
  | "end_break_time"
  | "end_time"
  | "id"
  | "is_break_time"
  | "shift_type"
  | "start_break_time"
  | "start_time"
> & {
  ad_year: number;
  approval_status: ApproveStatusType;
  day: number;
  leave_end_time: string;
  leave_start_time: string;
  month: number;
  template: number;
};

export type LeaveRequestType = Omit<LeaveRequestSerializer, "work_shift_detail"> & {
  created_by_full_name: string;
  employee_id: number;
  name: string;
  total_leave_days: number;
  work_shift_detail: {
    work_shift?: WorkShiftItemType[];
    work_shift_list: any;
  };
};

export type ClockRecordType = ClockRecordSerializer;

export type ShiftTimeWorkType = Partial<{
  end_break_time: string;
  end_work_time: string;
  start_break_time: string;
  start_work_time: string;
}>;

type AttendanceParams = {
  clockIn?: Moment | string;
  clockOut?: Moment | string;
  scheduledHours: number;
  shift: ShiftType;
};

type AttendanceResult = {
  actualWorkHour: string;
  actualWorkHours: number;
  breakLeaveOverlapHours: number;
  earlyOutMin: string;
  earlyOutMinutes: number;
  isEarlyOut: boolean;
  isLate: boolean;
  isOutsideShift: boolean;
  lateMin: string;
  lateMinutes: number;
  overtimeHours: number;
};

type TimeFieldsData = Record<string, number | string | null | undefined>;

type ValidationConfig<T extends TimeFieldsData> = {
  date?: string;
  endTime: Moment | string;
  fields: {
    end: string;
    start: string;
  }[];
  startTime: Moment | string;
  times: T;
};

type FormatTimeConfig = {
  date?: string;
  endTime?: string | null;
  isOutsideRange?: boolean;
  startTime?: string | null;
  timeValue?: string;
};

export type EditModeType = "ADD" | "APPROVE" | "DELETE" | "DUPLICATE" | "SELECT";

export type PickedState = PickMainState<
  | "adminMenuDivisions"
  | "buttonLoadCheck"
  | "errorMessage"
  | "searchedItemListWithKey"
  | "selectedDivision"
>;

export type MasterOptions = MasterMapOptions<typeof Masters>;

type FilterType = Partial<{ byEmployee?: boolean; division: number; employee: number | null }>;

export type AssignType = "" | "CUSTOM" | "OFF" | "TEMPLATE";

export type Event =
  | { message: "GetMasterData"; params: Record<string, unknown> }
  | { message: "RunSequence"; params: Record<string, unknown> };

export type Data = {
  device?: number;
  division?: number;
  masterData?: MasterMapData<typeof Masters>;
};

export type SeqState = BaseSeqState<SequenceName> & {
  byEmployee?: boolean;
  divisionId?: number;
  employee?: number;
};

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.ADD_EMPLOYEE]: {
    departmentId: number | null;
    employeeData: Record<string, any> | null;
    viewMonth: Moment;
  };
  [ACTIONS.ALL_APPROVE]: { viewMonth: Moment; onSuccess?: () => void };
  [ACTIONS.APPLY_NEW_TIME]: ApplyNewTimeParamsType;
  [ACTIONS.APPROVE_TIME]: ApproveTimeParamsType;
  [ACTIONS.CLOCK_RECORD]: ClockRecordParamsType;
  [ACTIONS.DUPLICATE]: DuplicateEmployeeType & {
    confirm?: boolean;
    viewMonth: Moment;
    onSuccess?: () => void;
  };
  [ACTIONS.EDIT_REGULAR_WORK]: EditRegularWorkParams;
  [ACTIONS.EMPLOYEE_ATTENDANCE_REPORT]: { viewMonth: Moment };
  [ACTIONS.INIT]: {
    refreshMode?: "all" | "current";
    viewMonth: Moment;
  };
  [ACTIONS.OPEN_HISTORY]: OpenHistoryParamsType;
  [ACTIONS.PRINT_WORK_SCHEDULE]: { viewMonth: Moment };
  [ACTIONS.REMOVE]: { employeeId: number; viewMonth: Moment; onSuccess?: () => void };
  [ACTIONS.SAVE]: { type: ActionSaveType; viewMonth: Moment };
  [ACTIONS.SEARCH]: {
    // refresh ใหม่ทั้งหมด | refresh แค่เดือน | ไม่ดึงซ้ำที่ดึงแล้ว
    refreshMode?: "all" | "current";
    viewMonth: Moment;
  };
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State,
  SeqState
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

/** ===================== Constants ====================  */
export const StateInitial: State = {
  SetupWorkScheduleSequence: null,
};

export const DataInitial: Data = {};

export const LEAVE_OPTION_MAPPING = {
  A: "", // leave ที่มีการ approve เวลาแล้ว
  AF: "Full Day",
  AH: "Half Day",
  F: "Full Day",
  H: "Half Day",
};

export const HOLIDAY_TAG = "HOLIDAY";

export const ASSIGN_SHIFT_TIME_OPTIONS = [
  {
    key: "TEMPLATE",
    text: "Select from Template",
    value: "TEMPLATE",
  },
  {
    key: "CUSTOM",
    text: "Custom time",
    value: "CUSTOM",
  },
  {
    key: "OFF",
    text: "OFF",
    value: "OFF",
  },
];

const Masters = [["leaveType", {}]] as const;

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  await controller.handleEvent({ message: "GetMasterData", params: { masters: Masters } });

  const state = controller.getState();

  controller.setState(
    {
      SetupWorkScheduleSequence: {
        ...state.SetupWorkScheduleSequence,
        sequenceIndex: "Action",
        filter: {
          byEmployee: params.byEmployee,
          division: params.divisionId,
          employee: params.employee,
        },
      },
    },
    () => {
      if (!params.divisionId) {
        console.log("Division ID is required");

        return;
      }

      Action(controller, {
        ...params,
        action: ACTIONS.INIT,
        refreshMode: "all",
        viewMonth: moment(),
      });
    }
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.INIT]: HandleInit,
    [ACTIONS.ADD_EMPLOYEE]: HandleAddEmployee,
    [ACTIONS.ALL_APPROVE]: HandleAllApprove,
    [ACTIONS.APPLY_NEW_TIME]: HandleApplyNewTime,
    [ACTIONS.APPROVE_TIME]: HandleApproveTime,
    [ACTIONS.CLOCK_RECORD]: HandleGetClockRecord,
    [ACTIONS.DUPLICATE]: HandleDuplicate,
    [ACTIONS.EDIT_REGULAR_WORK]: HandleEditRegularWork,
    [ACTIONS.EMPLOYEE_ATTENDANCE_REPORT]: HandleEmployeeAttendanceReport,
    [ACTIONS.OPEN_HISTORY]: HandleOpenHistory,
    [ACTIONS.PRINT_WORK_SCHEDULE]: HandlePrintWorkSchedule,
    [ACTIONS.REMOVE]: HandleRemove,
    [ACTIONS.SAVE]: HandleSave,
    [ACTIONS.SEARCH]: HandleSearch,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleInit: Handler<Params<Actions["INIT"]>> = async (controller, params) => {
  const state = controller.getState();

  const [constance] = await ConstanceView.get({
    apiToken: controller.apiToken,
    params: { list: "HRM_OT_CUTOFF_DATE" },
  });

  controller.setState(
    {
      SetupWorkScheduleSequence: {
        ...state.SetupWorkScheduleSequence,
        constance: { ...constance?.result },
      },
    },
    () => {
      Action(controller, { ...params, action: ACTIONS.SEARCH });
    }
  );
};

const HandleSearch: Handler<Params<Actions["SEARCH"]>, Promise<void>> = async (
  controller,
  params
) => {
  const state = controller.getState();

  const seq = state.SetupWorkScheduleSequence ?? {};
  const workShifts = seq.workShifts ?? {};
  const storeSlots = seq.storeSlots ?? {};
  const yearMonth = params.viewMonth.format("YYYY-MM");

  if (workShifts[yearMonth] && !params.refreshMode) {
    console.log("ไม่โหลดใหม่เนื่องจากจะได้ไม่ทับของที่แก้ไข");

    return;
  }

  const btnKey = params.refreshMode === "all" ? BTN_ACTS.SEARCH : `${BTN_ACTS.SEARCH}_CHANGE`;

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: BLC_STATUS.LOADING },
  });

  const { isCanceled, leaveRequest, payrollRecordEmployee, wsListItems, wsSettings, wsTemplates } =
    await GetWorkShiftData(controller, params);

  if (isCanceled) {
    return;
  }

  controller.setState({
    SetupWorkScheduleSequence: {
      ...seq,
      leaveRequests: { ...seq.leaveRequests, [yearMonth]: leaveRequest },
      payrollRecordEmployeeIds: {
        ...seq.payrollRecordEmployeeIds,
        [yearMonth]: payrollRecordEmployee,
      },
      storeSlots: Object.keys(storeSlots).length > 0 ? storeSlots : {},
      workShifts: { ...seq.workShifts, [yearMonth]: wsListItems },
      workShiftSettings: wsSettings,
      workShiftTemplates: wsTemplates,
      ...(params.refreshMode === "all" && {
        leaveRequests: { [yearMonth]: leaveRequest },
        payrollRecordEmployeeIds: { [yearMonth]: payrollRecordEmployee },
        storeSlots: {},
        workShifts: { [yearMonth]: wsListItems },
        workShiftSettings: wsSettings,
      }),
    },
  });

  controller.setState({ buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: null } });
};

const HandleDuplicate: Handler<Params<Actions["DUPLICATE"]>> = async (controller, params) => {
  const state = controller.getState();

  const btnKey = params.confirm ? `${BTN_ACTS.DUPLICATE}_CONFIRM` : BTN_ACTS.DUPLICATE;

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: BLC_STATUS.LOADING },
  });

  const seq = state.SetupWorkScheduleSequence ?? {};
  const yearMonth = params.viewMonth.format("YYYY-MM");
  const workShiftSettings = seq.workShiftSettings ?? [];
  const leaveRequests = seq.leaveRequests ?? {};
  const timeShifts = seq.workShifts?.[yearMonth] ?? [];
  const storeSlots = seq.storeSlots ?? {};
  const [year, month] = (params.yearMonth || "").split("-");

  // หา workShiftSetting สำหรับปี-เดือนที่เลือก
  const targetIndex = workShiftSettings.findIndex(
    (item) => item.ad_year === Number(year) && item.month === Number(month)
  );
  const currentWorkShiftSetting = workShiftSettings.find(
    (item) =>
      item.ad_year === params.viewMonth.year() && item.month === params.viewMonth.month() + 1
  );

  const workShiftSetting =
    params.type === "SHIFT_TIME"
      ? workShiftSettings[targetIndex]
      : { employees: workShiftSettings[targetIndex].employees };

  const employeeIds = workShiftSetting.employees.map((item) => item.employee);

  let filteredLeaveRequest = leaveRequests[yearMonth] ?? [];

  // Fetch leave request หากไม่มี Leave request ของ Employee นั้นๆใน State
  if (filteredLeaveRequest.length === 0) {
    const leaveRequest = await GetLeaveRequestList(controller, { employeeIds, yearMonth });

    filteredLeaveRequest = leaveRequest ?? [];

    // หากมี Leave request ให้เพิ่มกลับไปใน state
    leaveRequests[yearMonth]?.push(...filteredLeaveRequest);
  }

  // ดึงข้อมูล work shift list จากเดือนที่เลือกมาอยู่ในเดือนปัจจุบัน
  const { wsListItems } = await GetWorkShiftList(controller, {
    leaveRequest: filteredLeaveRequest,
    viewMonth: params.viewMonth,
    workShiftSetting: workShiftSetting as WorkShiftSettingType,
  });

  const maxDayOfMonth = Number(moment(params.yearMonth).endOf("month").format("D"));

  const filteredWsListItems = wsListItems.map((item) => ({
    ...item,
    events: item.events.filter((event) => event.start.date() <= maxDayOfMonth),
  }));

  storeSlots[yearMonth] ??= [];
  storeSlots[yearMonth]?.push({
    action: "DUPLICATE",
    timeShifts: timeShifts.map((item) => ({
      ...item,
      events: item.events.map((acc) => ({ ...acc, deleted: !!acc.id })),
    })),
    workShiftSettings: [...workShiftSettings],
  });

  const formattedWsSettings = workShiftSettings.map((item) =>
    item.ad_year === params.viewMonth.year() && item.month === params.viewMonth.month() + 1
      ? {
          ...item,
          employees: filteredWsListItems.map((acc) => ({
            id: undefined,
            employee: acc.employee,
            employee_code: acc.employee_code,
            employee_full_name: acc.employee_full_name,
          })),
          removes: currentWorkShiftSetting?.employees ?? [],
        }
      : item
  );

  controller.setState({
    SetupWorkScheduleSequence: {
      ...seq,
      leaveRequests,
      storeSlots: { ...storeSlots },
      workShifts: {
        ...seq.workShifts,
        [yearMonth]: filteredWsListItems.map((item) => ({
          ...item,
          events: item.events.map((event) => ({
            ...event,
            id: undefined,
            approval_status: "P",
            shift_status: ["L", "O"].includes(event.shift_status) ? event.shift_status : "W",
          })),
        })),
      },
      workShiftSettings: [...formattedWsSettings],
    },
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [btnKey]: params.confirm ? null : BLC_STATUS.SUCCESS,
    },
  });

  params.onSuccess?.();
};

const HandleAllApprove: Handler<Params<Actions["ALL_APPROVE"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.ALL_APPROVE]: BLC_STATUS.LOADING },
  });

  const workShiftSettings = state.SetupWorkScheduleSequence?.workShiftSettings ?? [];
  const yearMonth = params.viewMonth.format("YYYY-MM");
  const [year, month] = yearMonth.split("-");

  const workShiftSetting = workShiftSettings.find(
    (item) => item.ad_year === Number(year) && item.month === Number(month)
  );

  const [, error] = await WorkShiftSettingAutoApproveDetail.update({
    apiToken: controller.apiToken,
    pk: workShiftSetting?.id,
  });

  if (error) {
    SetErrorMessage(controller, { ...params, btnError: null, error });

    return;
  }

  await HandleSearch(controller, { ...params, action: ACTIONS.SEARCH, refreshMode: "current" });

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.ALL_APPROVE]: null },
  });

  params.onSuccess?.();
};

const HandleRemove: Handler<Params<Actions["REMOVE"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.REMOVE]: BLC_STATUS.LOADING },
  });

  const seq = state.SetupWorkScheduleSequence ?? {};
  const workShiftSettings = seq.workShiftSettings ?? [];
  const yearMonth = params.viewMonth.format("YYYY-MM");
  const timeShifts = seq.workShifts?.[yearMonth] ?? [];
  const index = timeShifts.findIndex((item) => item.employee === params.employeeId);
  const workShiftData = timeShifts[index];
  const [year, month] = yearMonth.split("-");

  let updatedStoreSlots = seq.storeSlots?.[yearMonth] ?? [];

  const settingIndex = workShiftSettings.findIndex(
    (item) => item.ad_year === Number(year) && item.month === Number(month)
  );

  if (settingIndex === -1) {
    console.error("Work shift setting not found");

    return;
  }

  const workShiftSetting = workShiftSettings[settingIndex];

  if (workShiftSetting.id) {
    const [, error] = await WorkShiftSettingDetail.patch({
      apiToken: controller.apiToken,
      data: { action: "REMOVE", confirm_deleting: true, employees: [workShiftData] },
      pk: workShiftSetting.id,
    });

    if (error) {
      SetErrorMessage(controller, { ...params, btnError: null, error });

      return;
    }
  }

  // ลบจาก time shifts
  timeShifts.splice(index, 1);
  //  ลบจาก work shift settings employees
  workShiftSettings[settingIndex].employees = workShiftSettings[settingIndex].employees.filter(
    (item) => item.employee !== workShiftData.employee
  );
  // ลบจาก store slots
  updatedStoreSlots = updatedStoreSlots.filter(
    (item) => item.data?.employee !== workShiftData.employee
  );

  controller.setState({
    SetupWorkScheduleSequence: {
      ...seq,
      storeSlots: { ...seq.storeSlots, [yearMonth]: [...updatedStoreSlots] },
      workShifts: { ...seq.workShifts, [yearMonth]: [...timeShifts] },
      workShiftSettings,
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.REMOVE]: null },
  });

  params.onSuccess?.();
};

const HandleSave: Handler<Params<Actions["SAVE"]>> = async (controller, params) => {
  const state = controller.getState();

  const btnKey = `${BTN_ACTS.SAVE}_${params.type}`;

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: BLC_STATUS.LOADING },
  });

  const seq = state.SetupWorkScheduleSequence ?? {};
  const workShifts = seq.workShifts ?? {};
  const workShiftSettings = seq.workShiftSettings ?? [];
  const storeSlots = seq.storeSlots ?? {};

  // Save แค่ work shift ของเดือนที่มีการแก้ไขหรือบันทึกแบบ blank to off ของเดือนนั้น
  // หรือมีการแก้ไข holiday
  const workShiftsWithSlots = Object.entries(workShifts).filter(([key, value]) => {
    const [year, month] = key.split("-");

    const workShiftSetting = workShiftSettings.find(
      (item) => item.ad_year === Number(year) && item.month === Number(month)
    );

    const hasEdit = (workShiftSetting?.holiday ?? []).some((acc) => !acc.active || !acc.day);
    const hasNew = !!value?.some((acc) => !acc.id);

    return (
      hasEdit ||
      hasNew ||
      !!storeSlots[key]?.length ||
      (params.type === "APPLY_BLANK_OFF" && key === params.viewMonth.format("YYYY-MM"))
    );
  });

  const savePromises = workShiftsWithSlots.map(async ([key, value]) => {
    const [year, month] = key.split("-");
    const workShiftSetting = workShiftSettings.find(
      (item) => item.ad_year === Number(year) && item.month === Number(month)
    );

    return CreateUpdateWorkShift(controller, {
      storeSlots: storeSlots[key] ?? [],
      timeShifts: (value ?? []).map((item) => ({
        ...item,
        events: item.events.map((event) => ({ ...event, actionLog: "Add" })),
      })),
      type: params.type,
      workShiftSetting,
    });
  });

  const saveResponses = await Promise.all(savePromises);
  const isError = saveResponses.length > 0 && saveResponses.every((item: any) => item[1]);

  if (isError) {
    SetErrorMessage(controller, {
      ...params,
      action: btnKey,
      error: saveResponses.map((item) => item[1]),
    });

    return;
  }

  const items = await GetWorkShiftSettingItems(controller);

  const fetchPromises = Object.entries(workShifts).map(async ([key]) => {
    const [year, month] = key.split("-");
    const workShiftSetting = items.find(
      (item) => item.ad_year === Number(year) && item.month === Number(month)
    );

    const { wsListItems } = await GetWorkShiftList(controller, {
      leaveRequest: seq.leaveRequests?.[key] ?? [],
      viewMonth: moment(key),
      workShiftSetting,
    });

    return [key, wsListItems];
  });

  const fetchResults = await Promise.all(fetchPromises);

  const workShiftsMap = Object.fromEntries(fetchResults);

  controller.setState({
    SetupWorkScheduleSequence: {
      ...state.SetupWorkScheduleSequence,
      storeSlots: {},
      workShifts: workShiftsMap,
      workShiftSettings: items,
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: BLC_STATUS.SUCCESS },
  });
};

const HandleGetClockRecord: Handler<Params<Actions["CLOCK_RECORD"]>, Promise<void>> = async (
  controller,
  params
) => {
  const state = controller.getState();

  if (params.isClear) {
    controller.setState({
      SetupWorkScheduleSequence: { ...state.SetupWorkScheduleSequence, clockRecords: [] },
    });

    return;
  }

  const [result] = await ClockRecordList.list({
    apiToken: controller.apiToken,
    params: {
      clock_date: params.start.format("DD/MM/YYYY"),
      employee: params.employee,
      shift_type_night: params.shift_type === "N",
    },
  });

  const items: ClockRecordSerializer[] = result?.items || [];
  const filteredItems = items.filter((item) => item.status !== "I");

  controller.setState({
    SetupWorkScheduleSequence: { ...state.SetupWorkScheduleSequence, clockRecords: filteredItems },
  });
};

const HandleApplyNewTime: Handler<Params<Actions["APPLY_NEW_TIME"]>> = async (
  controller,
  params
) => {
  const { end_work_time: endWorkTime, start_work_time: startWorkTime, ...res } = params.data;

  const startDate = params.event.start.format("YYYY-MM-DD");
  const startTime = moment(`${startDate} ${startWorkTime}`);
  const endTime = moment(`${startDate} ${endWorkTime}`);

  // สร้าง event data เพื่อ update เวลา
  const eventData: EventsTimeShiftType = {
    ...params.event,
    ...res,
    actionLog: "Edited",
    end: endTime,
    is_apply_new_time: true,
    is_break_time: !!params.data.start_break_time && !!params.data.end_break_time,
    shift_type: endTime.isBefore(startTime) ? "N" : "D",
    start: startTime,
    template: undefined,
    title: "",
  };

  HandleWorkScheduleUpdate(controller, { ...params, data: eventData });
};

const HandleApproveTime: Handler<Params<Actions["APPROVE_TIME"]>> = async (controller, params) => {
  // สร้าง event data เพื่อ approve เวลา
  const eventData: EventsTimeShiftType = {
    ...params.event,
    ...params.data,
    // APPROVE
    ...(params.data.clock_in &&
      params.data.clock_out && {
        approval_status: "A",
        shift_status: "W",
      }),
    // ABSENT
    ...((!params.data.clock_in || !params.data.clock_out) && {
      approval_status: "A",
      shift_status: "X",
    }),
    // APPROVE ลางาน
    ...(params.isFullDayLeave && {
      approval_status: "A",
      shift_status: "L",
    }),
    // OFF
    ...(params.isScheduledOff && {
      approval_status: "A",
      // ถ้ายังไม่ได้สร้าง work shift ให้บันทึก work shift ด้วย shift_status เดิมก่อนแล้วค่อยอัพเดต shift status เป็น "O"
      shift_status: params.event.id ? "O" : params.event.shift_status,
    }),
    // UNAPPROVE
    ...(params.event.approval_status === "A" && {
      approval_status: "P",
    }),
    is_approve: true,
  };

  HandleWorkScheduleUpdate(controller, { ...params, data: eventData });
};

const HandleAddEmployee: Handler<Params<Actions["ADD_EMPLOYEE"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.ADD_EMPLOYEE]: BLC_STATUS.LOADING },
  });

  const employees = await GetEmployeeForWorkShifts(controller, params);
  const employeeIds = new Set(employees.map((employee) => Number(employee.id)));

  const seq = state.SetupWorkScheduleSequence ?? {};
  const workShiftSettings = seq.workShiftSettings ?? [];
  const workShifts = seq.workShifts ?? {};
  const leaveRequests = seq.leaveRequests ?? {};

  const yearMonth = params.viewMonth.format("YYYY-MM");
  const [year, month] = yearMonth.split("-");

  const updatedWorkShifts: WorkShiftType[] = employees.map((employee) => ({
    id: undefined,
    employee: employee.id ?? 0,
    employee_code: employee.code || "",
    employee_full_name: employee.full_name || "",
    events: [],
    logs: [],
    regular_work_hours: null,
  }));

  let filteredLeaveRequest = (leaveRequests[yearMonth] ?? []).filter((item) =>
    employeeIds.has(item.employee_id)
  );

  // Fetch leave request หากไม่มี Leave request ของ Employee นั้นๆใน State
  if (filteredLeaveRequest.length === 0) {
    const leaveRequest = await GetLeaveRequestList(controller, {
      employeeIds: [...employeeIds],
      yearMonth,
    });

    filteredLeaveRequest = leaveRequest ?? [];

    // หากมี Leave request ให้เพิ่มกลับไปใน state
    leaveRequests[yearMonth]?.push(...filteredLeaveRequest);
  }

  // อัปเดต workShiftSettings โดยเพิ่มพนักงานเข้าไปในปี-เดือนที่ตรงกัน
  const formattedWsSettings = workShiftSettings.map((item) =>
    item.ad_year === Number(year) && item.month === Number(month)
      ? {
          ...item,
          employees: [
            ...item.employees,
            ...updatedWorkShifts.map((ws) => ({
              employee: ws.employee,
              employee_code: ws.employee_code,
              employee_full_name: ws.employee_full_name,
            })),
          ],
        }
      : item
  );

  const merged = mergeWorkShiftLeaveRequestItems(updatedWorkShifts, filteredLeaveRequest);

  workShifts[yearMonth]?.push(...merged);

  controller.setState({
    SetupWorkScheduleSequence: {
      ...seq,
      leaveRequests,
      workShifts: { ...workShifts },
      workShiftSettings: formattedWsSettings,
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.ADD_EMPLOYEE]: null },
  });
};

const HandleWorkScheduleUpdate: Handler<
  Omit<ApplyNewTimeParamsType, "data"> & {
    sequence: SequenceName;
    data: EventsTimeShiftType;
    isScheduledOff?: boolean;
  }
> = async (controller, params) => {
  let state = controller.getState();

  const seq = state.SetupWorkScheduleSequence ?? {};
  const workShiftSettings = seq.workShiftSettings ?? [];
  const storeSlots = seq.storeSlots ?? {};
  const workShifts = seq.workShifts ?? {};
  const { id, employee, end, start } = params.event;
  const startTime = start.format("YYYY-MM-DD");
  const [year, month] = startTime.split("-");
  const key = `${year}-${month}`;

  // หา workShiftSetting สำหรับปี-เดือนที่เลือก
  const index = workShiftSettings.findIndex(
    (item) => item.ad_year === Number(year) && item.month === Number(month)
  );

  const timeShift: WorkShiftType = {
    id: undefined,
    employee,
    employee_code: "",
    employee_full_name: "",
    events: [params.data],
    logs: [],
    regular_work_hours: null,
  };

  // หา slot ที่มี event ของ employee คนนี้ในวันเดียวกันและมีสถานะ "O" (Off/หยุด)
  const slotOffIndex = (storeSlots[key] ?? []).findIndex((item) =>
    item.events?.find(
      (event) =>
        event.employee === employee &&
        start.isSame(event.start, "days") &&
        event.shift_status === "O"
    )
  );
  // หา slot ที่มี event ของ employee คนนี้ในวันเดียวกันและ start, end ตรงกัน
  const slotIndex = (storeSlots[key] ?? []).findIndex(
    (item) =>
      item.event?.employee === employee &&
      start.isSame(item.event.start) &&
      end.isSame(item.event.end)
  );

  const [result, error] = await CreateUpdateWorkShift(controller, {
    isScheduledOff: params.isScheduledOff,
    storeSlots: slotOffIndex === -1 ? [] : [(storeSlots[key] ?? [])[slotOffIndex]],
    timeShifts: [timeShift],
    type: "NO_BLANK_OFF",
    workShiftSetting: index === -1 ? undefined : { ...workShiftSettings[index], holiday: [] },
  });

  if (error) {
    params.onError();

    return;
  }

  const detail = await GetWorkShiftDetail(controller, {
    workShiftSetting: workShiftSettings[index],
    ...params.data,
  });

  if (slotOffIndex !== -1) {
    // ลบ slot off จาก store เพราะมีการบันทึกข้อมูลลง database
    storeSlots[key]?.splice(slotOffIndex, 1);
  }

  if (slotIndex !== -1) {
    storeSlots[key]?.splice(slotIndex, 1);
  }

  workShiftSettings[index].id = result?.workShiftSetting?.id;
  workShiftSettings[index].employees = result?.workShiftSetting?.employees ?? [];

  // อัปเดต workShifts ในส่วนของ employee ที่เลือก
  workShifts[key] = workShifts[key]?.map((item) => {
    if (item.employee === employee && detail) {
      const foundEventIndex = item.events.findIndex((event) =>
        event.work_shift?.some((ws) => ws.id === id)
      );

      // Leave request ต้อง update shift_time_id เมื่อมีการ Update workshift
      if (foundEventIndex !== -1) {
        const workShiftIndex = (item.events[foundEventIndex].work_shift ?? []).findIndex(
          (ws) => ws.id === id
        );

        if (workShiftIndex !== -1) {
          const workShift = item.events[foundEventIndex].work_shift;

          if (workShift?.[workShiftIndex]) {
            workShift[workShiftIndex].id = detail.id;
          }
        }
      }

      const formattedDetail = formatWorkShiftEvent(detail, params.data.start);

      item.events[params.index] = formattedDetail;
      // push logs ทุกครั้งที่มีการอัพเดต
      item.logs.push(formattedDetail);
    }

    return item;
  });

  if (params.data.is_approve && params.data.approval_status === "P") {
    await HandleGetClockRecord(controller, {
      ...params,
      ...params.event,
      action: ACTIONS.CLOCK_RECORD,
    });
  }

  state = controller.getState();

  controller.setState({
    SetupWorkScheduleSequence: {
      ...state.SetupWorkScheduleSequence,
      storeSlots: { ...storeSlots },
      workShifts: { ...workShifts },
      workShiftSettings: [...workShiftSettings],
    },
  });

  params.onSuccess();
};

const HandleEditRegularWork: Handler<Params<Actions["EDIT_REGULAR_WORK"]>> = async (
  controller,
  params
) => {
  const state = controller.getState();

  const btnKey = `${BTN_ACTS.EDIT_REGULAR_WORK}_${params.data.employee}`;

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: BLC_STATUS.LOADING },
  });

  const seq = state.SetupWorkScheduleSequence ?? {};
  const workShiftSettings = seq.workShiftSettings ?? [];
  const workShifts = seq.workShifts ?? {};

  const index = workShiftSettings.findIndex(
    (item) =>
      item.ad_year === params.viewMonth?.year() && item.month === params.viewMonth.month() + 1
  );

  const detail = await CreateUpdateWorkShiftSetting(controller, {
    workShiftSetting: {
      ...workShiftSettings[index],
      employees: [{ ...params.data, regular_work_hours: params.value }],
      holiday: [],
    },
  });

  if (!detail) {
    SetErrorMessage(controller, {
      btnAction: btnKey,
      btnError: null,
      card: "ไม่สามารถแก้ไขได้",
      errorKey: params.sequence,
    });

    return;
  }

  workShiftSettings[index].id = detail.id;
  workShiftSettings[index].employees = workShiftSettings[index].employees.map((item) => {
    if (item.employee === params.data.employee) {
      const employee = detail.employees.find((acc) => acc.employee === item.employee);

      return employee ?? item;
    }

    return item;
  });

  const key = moment(`${detail.ad_year}-${detail.month}`).format("YYYY-MM");

  // อัปเดต workShifts ในส่วนของ employee ที่เลือก
  workShifts[key] = workShifts[key]?.map((item) =>
    item.employee === params.data.employee ? { ...item, regular_work_hours: params.value } : item
  );

  controller.setState({
    SetupWorkScheduleSequence: {
      ...seq,
      workShifts: { ...workShifts },
      workShiftSettings: [...workShiftSettings],
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: null },
  });
};

const HandleEmployeeAttendanceReport: Handler<
  Params<Actions["EMPLOYEE_ATTENDANCE_REPORT"]>
> = async (controller, params) => {
  const state = controller.getState();
  const actionKey = BTN_ACTS.EMPLOYEE_ATTENDANCE_REPORT;

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [actionKey]: BLC_STATUS.LOADING },
  });

  const month = params.viewMonth.month() + 1;
  const year = params.viewMonth.year() + 543;

  const [data] = await WorkShiftAttendanceReportPipelineView.get({
    apiToken: controller.apiToken,
    params: {
      month,
      unit: state.SetupWorkScheduleSequence?.filter?.division,
      year,
    },
  });

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [actionKey]: BLC_STATUS.SUCCESS },
  });

  const formsPromises: any[] = [FormEmployeeAttendanceReport({ ...data })];
  const forms = await Promise.all(formsPromises);
  const bloburl = await combinePdfFiles(forms);

  globalThis.open(bloburl);
};

const HandlePrintWorkSchedule: Handler<Params<Actions["PRINT_WORK_SCHEDULE"]>> = async (
  controller,
  params
) => {
  const state = controller.getState();
  const actionKey = BTN_ACTS.PRINT_WORK_SCHEDULE;

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [actionKey]: BLC_STATUS.LOADING },
  });

  const month = params.viewMonth.month() + 1;
  const year = params.viewMonth.year() + 543;

  const [data] = await WorkShiftScheduleReportPipelineView.get({
    apiToken: controller.apiToken,
    params: {
      month,
      unit: state.SetupWorkScheduleSequence?.filter?.division,
      year,
    },
  });

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [actionKey]: BLC_STATUS.SUCCESS },
  });

  const formsPromises: any[] = [FormWorkScheduleReport({ ...data })];
  const forms = await Promise.all(formsPromises);
  const bloburl = await combinePdfFiles(forms);

  globalThis.open(bloburl);
};

const HandleOpenHistory: Handler<Params<Actions["OPEN_HISTORY"]>> = async (controller, params) => {
  const state = controller.getState();

  const btnKey = `${params.employee}-${params.date.format("YYYY-MM-DD")}`;

  controller.setState({ buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "LOADING" } });

  const workShifts = state.SetupWorkScheduleSequence?.workShifts ?? {};
  const allWorkShifts = Object.values(workShifts).flat();
  const allShiftLogs = allWorkShifts.flatMap((shift) => shift?.logs ?? []);

  const filteredEvents = allShiftLogs.filter(
    (event) => event.start.isSame(params.date, "day") && event.employee === params.employee
  );

  const historyPromises = filteredEvents.map(async (event) => {
    const historyListPromise: Promise<[{ items: WorkShiftHistoryResponseType[] } | null, unknown]> =
      WorkShiftHistoryList.list({
        apiToken: controller.apiToken,
        params: { id: event.id },
      });

    return historyListPromise;
  });

  const responses = await Promise.all(historyPromises);
  const mergedItems = responses.flatMap((response) => response[0]?.items ?? []);
  const allItems = mergedItems.flatMap((item, index) =>
    // ถ้าเป็น Edited จะเอาข้อมูลก่อนก่อนมาแสดงด้วย
    item.log_detail.action === "Edited" && !item.log_detail.note
      ? [
          {
            ...mergedItems[index - 1],
            created: item.created,
            log_detail: { action: "Edited" as const },
          },
          item,
        ]
      : [item]
  );

  const grouped: Record<string, WorkShiftHistoryType | undefined> = {};

  for (const item of allItems) {
    let action: WorkShiftHistoryType["shifts"][number]["action"] = item.log_detail.action;

    const key = moment(item.created).format("YYYY-MM-DD HH:mm");

    const isEdit = action === "Edited";

    if (isEdit && item.log_detail.note === "approval_status : Approved") {
      action = "Approve";
    } else if (isEdit && item.log_detail.note === "approval_status : Pending") {
      action = "Unapprove";
    }

    grouped[key] ??= {
      id: key,
      created: item.created,
      edit_user_full_name: item.edit_user_full_name || "",
      shifts: [],
    };

    const data = {
      action,
      created: item.created,
      end_break_time:
        item.template_detail?.end_break_time || item.manual_template_end_break_time || "",
      end_time: item.template_detail?.end_time || item.manual_template_end_time || "",
      is_break_time: !!item.template_detail?.is_break_time || !!item.manual_template_is_break_time,
      name: item.template_detail?.name || "",
      start_break_time:
        item.template_detail?.start_break_time || item.manual_template_start_break_time || "",
      start_time: item.template_detail?.start_time || item.manual_template_start_time || "",
    };

    const totalHours = calculateNetWorkingHours(data.start_time, data.end_time, data);

    grouped[key]?.shifts.push({ ...data, total_hours: totalHours });
  }

  const filteredHistory = Object.values(grouped).filter(
    (value): value is WorkShiftHistoryType => !!value
  );

  controller.setState({
    SetupWorkScheduleSequence: {
      ...state.SetupWorkScheduleSequence,
      workShiftHistory: filteredHistory,
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: null },
  });
};

/** ====================================================  */
/**                         APIs;                         */
/** ====================================================  */
const GetWorkShiftDetail: Handler<
  {
    color_code?: string;
    employee: number;
    end: Moment;
    shift_status?: EventsTimeShiftType["shift_status"];
    start: Moment;
    workShiftSetting?: WorkShiftSettingType;
  },
  Promise<WorkShiftResponseType | undefined>
> = async (controller, params) => {
  const day = Number(params.start.format("D"));

  const [wsList] = await WorkShiftList.list({
    apiToken: controller.apiToken,
    params: {
      day,
      employee: params.employee,
      limit: 99999,
      work_shift_setting: params.workShiftSetting?.id,
    },
  });

  const wsListItems: WorkShiftResponseType[] = wsList?.items || [];

  const findWorkShift = (matchCondition: (item: WorkShiftResponseType) => boolean) =>
    wsListItems.find((item) => {
      const isMatchedEmployeeDay = item.day === day && Number(item.employee_id) === params.employee;

      if (!isMatchedEmployeeDay) {
        return false;
      }

      const isMatched = matchCondition(item);

      return params.shift_status === "O" ? item.shift_status === "O" : isMatched;
    });

  // หาด้วย time ก่อน
  const result = findWorkShift((item) => {
    const startTime = item.template_detail?.start_time || item.manual_template_start_time;
    const endTime = item.template_detail?.end_time || item.manual_template_end_time;

    return (
      startTime === params.start.format("HH:mm:ss") && endTime === params.end.format("HH:mm:ss")
    );
  });

  // Fallback: หาด้วย colorCode ถ้าหา time ไม่เจอ
  if (!result && params.color_code) {
    return findWorkShift((item) => {
      const colorCode = item.template_detail?.color_code || item.manual_template_color_code;

      return colorCode === params.color_code;
    });
  }

  return result;
};

const GetLeaveRequestList: Handler<
  { abortCtrl?: AbortController; employeeIds: number[]; yearMonth: string },
  Promise<LeaveRequestType[] | null>
> = async (controller, params) => {
  const state = controller.getState();
  const filter = state.SetupWorkScheduleSequence?.filter ?? {};

  const startDate = moment(params.yearMonth).startOf("month").format("DD/MM/YYYY");
  const endDate = moment(params.yearMonth).endOf("month").format("DD/MM/YYYY");

  if (params.employeeIds.length === 0) {
    return [];
  }

  const [result, , network] = await LeaveRequestList.list({
    apiToken: controller.apiToken,
    params: {
      end_date: endDate,
      limit: 99999,
      start_date: startDate,
      ...(filter.byEmployee
        ? { employee_list: filter.employee }
        : { employee_list: params.employeeIds }),
    },
    extra: { signal: params.abortCtrl?.signal },
  });

  const leaveTypeData = controller.data.masterData?.leaveType ?? [];

  const items: LeaveRequestType[] = result?.items || [];
  const filteredItems = items.filter((item) => ["A", "P"].includes(item.status || ""));

  if (network.code === AxiosError.ERR_CANCELED) {
    return null;
  }

  return filteredItems.map((item) => {
    const leaveType = leaveTypeData.find((acc) => acc.id === item.leave_type);

    return {
      ...item,
      created_by_full_name: item.created_by_full_name,
      name: leaveType?.name || "",
      total_leave_days: item.total_leave_days,
    };
  });
};

const GetWorkShiftList: Handler<
  {
    leaveRequest: LeaveRequestType[];
    viewMonth: Moment;
    workShiftSetting?: WorkShiftSettingType;
  },
  Promise<{ wsListItems: WorkShiftType[] }>
> = async (controller, params) => {
  if (!params.workShiftSetting?.id) {
    const items = formatWorkShiftItems(
      [],
      params.workShiftSetting?.employees ?? [],
      params.viewMonth
    );

    return { wsListItems: mergeWorkShiftLeaveRequestItems(items, params.leaveRequest) };
  }

  const state = controller.getState();
  const filter = state.SetupWorkScheduleSequence?.filter ?? {};

  const [wsList] = await WorkShiftList.list({
    apiToken: controller.apiToken,
    params: {
      include_deleted: true,
      limit: 99999,
      work_shift_setting: params.workShiftSetting.id,
      ...(filter.byEmployee && { employee: filter.employee }),
    },
  });

  const items: WorkShiftResponseType[] = wsList?.items || [];

  const formattedItems = formatWorkShiftItems(
    items,
    params.workShiftSetting.employees,
    params.viewMonth
  );

  return { wsListItems: mergeWorkShiftLeaveRequestItems(formattedItems, params.leaveRequest) };
};

const CreateUpdateWorkShift: Handler<
  {
    isScheduledOff?: boolean;
    storeSlots: StoreSlotType[];
    timeShifts: WorkShiftType[];
    type: ActionSaveType;
    workShiftSetting: WorkShiftSettingType | undefined;
  },
  Promise<[Record<string, any> | null, unknown]>
> = async (controller, params) => {
  const workShiftSetting = await CreateUpdateWorkShiftSetting(controller, params);

  if (!workShiftSetting?.id) {
    return [null, null];
  }

  const listDaysOfMonth = getDaysOfMonth(
    moment(`${workShiftSetting.ad_year}-${workShiftSetting.month}`)
  );

  const flattedStoreSlots = params.storeSlots
    .flatMap((item) => [
      item.event,
      ...(item.events ?? []),
      ...(item.timeShifts ?? []).flatMap((acc) => acc.events),
    ])
    .filter((item): item is EventsTimeShiftType => !!item);

  const deletedItems = flattedStoreSlots
    .filter(
      (item): item is EventsTimeShiftType =>
        !!item.deleted &&
        !(workShiftSetting.removes ?? []).map((acc) => acc.employee).includes(item.employee) &&
        !!item.id
    )
    .map((item) => ({ ...item, actionLog: "Deleted" as const }));

  // เมื่อเป็น Slot ที่มาจากการบันทึก Shift time
  const isAddSlot = params.timeShifts[0]?.events?.[0]?.actionLog === "Add";

  const filteredTimeShifts = isAddSlot
    ? // กรอง Update เฉพาะข้อมูลที่มีการเปลี่ยนแปลง
      params.timeShifts.map((item) => ({
        ...item,
        events: item.events.filter((event) =>
          flattedStoreSlots.some(
            (store) =>
              store.employee === event.employee &&
              store.start.isSame(event.start) &&
              store.end.isSame(store.end)
          )
        ),
      }))
    : params.timeShifts;

  const flattedTimeShifts = filteredTimeShifts
    .flatMap((item) =>
      applyBlankToOff({
        employee: item.employee,
        events: item.events,
        listDaysOfMonth,
        type: params.type,
      })
    )
    .filter((item) => !item.leave_option);

  const formatEvents = (item: EventsTimeShiftType) => {
    const workShiftSettingEmployee = workShiftSetting.employees.find(
      (acc) => acc.employee === item.employee
    );

    return {
      action: item.actionLog,
      id: item.id,
      approval_status: item.approval_status,
      clock_in: item.clock_in,
      clock_out: item.clock_out,
      day: "start" in item ? Number(item.start.format("D")) : 1,
      deleted: item.deleted,
      // employee: item.employee,
      OT_clock_in_1_5hr: item.OT_clock_in_1_5hr || null,
      OT_clock_in_1hr: item.OT_clock_in_1hr || null,
      OT_clock_in_3hr: item.OT_clock_in_3hr || null,
      OT_clock_out_1_5hr: item.OT_clock_out_1_5hr || null,
      OT_clock_out_1hr: item.OT_clock_out_1hr || null,
      OT_clock_out_3hr: item.OT_clock_out_3hr || null,
      shift_status: item.shift_status,
      template: item.template,
      work_shift_setting_employee: workShiftSettingEmployee?.id,
      ...(!item.template && {
        manual_template_color_code: item.color_code || "",
        manual_template_end_break_time: item.end_break_time || null,
        manual_template_end_time: "end" in item ? item.end.format("HH:mm") : null,
        manual_template_is_break_time: item.is_break_time ?? false,
        manual_template_shift_type: item.shift_type || "",
        manual_template_start_break_time: item.start_break_time || null,
        manual_template_start_time: "start" in item ? item.start.format("HH:mm") : null,
      }),
    };
  };

  const formattedItems = [...flattedTimeShifts, ...deletedItems].map((element) =>
    formatEvents(element)
  );

  const firstTimeShift = flattedTimeShifts[0] as EventsTimeShiftType | undefined;
  const isUpdateApproval = firstTimeShift?.is_approve && firstTimeShift.id;

  // หากเป็นการ Apply new time ให้มีการดึง id ล่าสุดมาเนื่องจากอาจมีการแก้ไขพร้อมกัน
  if (firstTimeShift?.is_apply_new_time) {
    const detail = await GetWorkShiftDetail(controller, { ...firstTimeShift, workShiftSetting });

    formattedItems[0].id = detail?.id ?? formattedItems[0].id;
  }

  let workShiftDetail = null;
  let [, error]: any[] = isUpdateApproval
    ? []
    : await WorkShiftList.create({
        apiToken: controller.apiToken,
        data: { items: formattedItems, work_shift_setting: workShiftSetting.id } as any,
      });

  // หากเป็นการ Approve ให้เรียก api approve
  if (firstTimeShift?.is_approve) {
    // ถ้าเป็น approve off โดยที่ยังไม่ได้บันทึก work shift
    const shiftStatus =
      params.isScheduledOff && !firstTimeShift.id ? "O" : formattedItems[0].shift_status;

    [workShiftDetail, error] = await UpdateWorkShiftDetail(controller, {
      ...firstTimeShift,
      data: { ...formattedItems[0], shift_status: shiftStatus },
      workShiftSetting,
    });
  }

  return [{ workShiftDetail, workShiftSetting }, error];
};

const UpdateWorkShiftDetail: Handler<
  {
    data: Record<string, any>;
    employee: number;
    end: Moment;
    start: Moment;
    workShiftSetting?: WorkShiftSettingType;
  },
  Promise<any[]>
> = async (controller, params) => {
  // ต้องดึง id ล่าสุดเนื่องจากอาจมีการแก้ไขพร้อมกัน
  const detail = await GetWorkShiftDetail(controller, params);

  return WorkShiftDetail.update({
    apiToken: controller.apiToken,
    data: params.data as any,
    pk: detail?.id ?? params.data.id,
  }) as Promise<any[]>;
};

const CreateUpdateWorkShiftSetting: Handler<
  {
    workShiftSetting: WorkShiftSettingType | undefined;
  },
  Promise<WorkShiftSettingType | undefined>
> = async (controller, params) => {
  let result = params.workShiftSetting;

  const holidays = params.workShiftSetting?.holiday ?? [];

  const activeHolidays = holidays
    .filter((item) => item.active && !item.day)
    .map((item) => ({
      day: Number(item.start.format("D")),
      name: item.resource,
    }));

  const inactiveHolidays = holidays
    .filter((item) => !item.active)
    .map((item) => ({
      day: Number(item.start.format("D")),
      name: item.resource,
    }));

  const requestData = {
    ...params.workShiftSetting,
    employees: params.workShiftSetting?.employees ?? [],
    holiday: activeHolidays,
  };
  const removes = params.workShiftSetting?.removes ?? [];

  if (result?.id) {
    // ลบวันหยุดที่ถูกปิดการใช้งาน
    if (inactiveHolidays.length > 0) {
      await WorkShiftSettingDetail.patch({
        apiToken: controller.apiToken,
        data: { action: "REMOVE", holiday: inactiveHolidays },
        pk: result.id,
      });
    }

    if (activeHolidays.length > 0) {
      await WorkShiftSettingDetail.patch({
        apiToken: controller.apiToken,
        data: { action: "ADD", holiday: activeHolidays },
        pk: result.id,
      });
    }

    if (removes.length > 0) {
      await WorkShiftSettingDetail.patch({
        apiToken: controller.apiToken,
        data: {
          action: "REMOVE",
          confirm_deleting: true,
          employees: removes,
        },
        pk: result.id,
      });
    }

    if (requestData.employees.length > 0) {
      // เพิ่มวันหยุดที่ใช้งานอยู่
      [result] = await WorkShiftSettingDetail.patch({
        apiToken: controller.apiToken,
        data: { action: "ADD", employees: requestData.employees },
        pk: result.id,
      });
    }
  } else if (requestData.employees.length > 0) {
    [result] = await WorkShiftSettingList.create({
      apiToken: controller.apiToken,
      data: requestData,
    });
  }

  return result ? { ...result, removes } : result;
};

const GetWorkShiftData: Handler<
  { viewMonth: Moment },
  Promise<{
    isCanceled: boolean;
    leaveRequest: LeaveRequestType[];
    payrollRecordEmployee: PayrollRecordEmployeeType[];
    wsListItems: WorkShiftType[];
    wsSettings: WorkShiftSettingType[];
    wsTemplates: WorkShiftTemplateType[];
  }>
> = async (controller, params) => {
  const state = controller.getState();

  const yearMonth = params.viewMonth.format("YYYY-MM");
  const [year, month] = yearMonth.split("-");

  state.SetupWorkScheduleSequence?.abortCtrl?.abort();

  const abortCtrl = new AbortController();

  controller.setState({
    SetupWorkScheduleSequence: { ...state.SetupWorkScheduleSequence, abortCtrl },
  });

  const [{ leaveRequest, wsSettings }, wsTemplate, payroll] = await Promise.all([
    GetWorkShiftSettingLeaveRequestList(controller, { ...params, abortCtrl, yearMonth }),
    GetWorkShiftTemplateList(controller, { abortCtrl }),
    GetPayrollRecordList(controller, { abortCtrl, yearMonth }),
  ]);

  const workShiftSetting = wsSettings.find(
    (item) => item.ad_year === Number(year) && item.month === Number(month)
  );

  const templates = wsTemplate[0];

  const { wsListItems } = await GetWorkShiftList(controller, {
    leaveRequest: leaveRequest ?? [],
    viewMonth: params.viewMonth,
    workShiftSetting,
  });

  const isCanceled =
    !leaveRequest ||
    wsTemplate[2].code === AxiosError.ERR_CANCELED ||
    payroll[2].code === AxiosError.ERR_CANCELED;

  return {
    isCanceled,
    leaveRequest: leaveRequest ?? [],
    payrollRecordEmployee: payroll[0],
    wsListItems,
    wsSettings,
    wsTemplates: templates,
  };
};

const GetWorkShiftSettingList: Handler<
  { refreshMode?: "all" | "current"; viewMonth: Moment },
  Promise<WorkShiftSettingType[]>
> = async (controller, params) => {
  const state = controller.getState();
  const seq = state.SetupWorkScheduleSequence ?? {};
  const filter = seq.filter ?? {};
  const division = filter.division;

  if (!division) {
    console.error("Division is required for work shift settings");

    return [];
  }

  const yearMonth = params.viewMonth.format("YYYY-MM");
  const [year, month] = yearMonth.split("-");

  const ensureWorkShiftSetting = (settingItems: WorkShiftSettingType[]) => {
    const existingSetting = settingItems.find(
      (item) => item.ad_year === Number(year) && item.month === Number(month)
    );

    if (!existingSetting) {
      settingItems.push({
        active: true,
        ad_year: Number(year),
        division,
        employees: [],
        holiday: [],
        month: Number(month),
      });
    }

    return settingItems;
  };

  if (
    Array.isArray(seq.workShiftSettings) &&
    seq.workShiftSettings[0]?.division === filter.division &&
    params.refreshMode !== "all"
  ) {
    return ensureWorkShiftSetting([...seq.workShiftSettings]);
  }

  const items = await GetWorkShiftSettingItems(controller);

  return ensureWorkShiftSetting(items);
};

const GetWorkShiftSettingLeaveRequestList: Handler<
  {
    abortCtrl: AbortController;
    refreshMode?: "all" | "current";
    viewMonth: Moment;
    yearMonth: string;
  },
  Promise<{ leaveRequest: LeaveRequestType[] | null; wsSettings: WorkShiftSettingType[] }>
> = async (controller, params) => {
  const wsSettings = await GetWorkShiftSettingList(controller, params);

  const yearMonth = params.viewMonth.format("YYYY-MM");
  const [year, month] = yearMonth.split("-");

  const workShiftSetting = wsSettings.find(
    (item) => item.ad_year === Number(year) && item.month === Number(month)
  );

  const employeeIds = (workShiftSetting?.employees ?? []).map((item) => item.employee);

  const leaveRequest = await GetLeaveRequestList(controller, { ...params, employeeIds });

  return { leaveRequest, wsSettings };
};

const GetWorkShiftTemplateList: Handler<
  { abortCtrl: AbortController },
  Promise<[WorkShiftTemplateType[], unknown, any]>
> = async (controller, params) => {
  const [result, error, network] = await WorkShiftTemplateList.list({
    apiToken: controller.apiToken,
    extra: { signal: params.abortCtrl.signal },
  });

  const items: WorkShiftTemplateType[] = result?.items || [];

  const filteredItems = items.filter((item) => item.active);
  const formattedItems = filteredItems.map((item) => ({
    ...item,
    end_time: moment(item.end_time, "HH:mm:ss").format("HH:mm"),
    start_time: moment(item.start_time, "HH:mm:ss").format("HH:mm"),
  }));

  return [formattedItems, error, network];
};

const GetPayrollRecordList: Handler<
  { abortCtrl: AbortController; yearMonth: string },
  Promise<[PayrollRecordEmployeeType[], unknown, any]>
> = async (controller, params) => {
  const state = controller.getState();
  const filter = state.SetupWorkScheduleSequence?.filter ?? {};
  const otCutoffDate = state.SetupWorkScheduleSequence?.constance?.HRM_OT_CUTOFF_DATE ?? 0;

  const [year, month] = params.yearMonth.split("-");

  const [result, error, network] = await PayrollRecordList.list({
    apiToken: controller.apiToken,
    params: {
      ad_year: year,
      division: filter.division,
      month,
    },
    extra: { signal: params.abortCtrl.signal },
  });

  const items: PayrollRecordSerializer[] = result?.items || [];

  const formattedItems = items.flatMap((item) =>
    Array.from({ length: otCutoffDate }, (unused, index) => ({
      date: moment(`${year}-${month}-${index + 1}`),
      employee: Number(item.employee_id),
    }))
  );

  return [formattedItems, error, network];
};

const GetWorkShiftSettingItems: Handler<unknown, Promise<WorkShiftSettingType[]>> = async (
  controller
) => {
  const state = controller.getState();
  const filter = state.SetupWorkScheduleSequence?.filter ?? {};

  const [result] = await WorkShiftSettingList.list({
    apiToken: controller.apiToken,
    params: { division: filter.division, ...(filter.byEmployee && { employee: filter.employee }) },
  });

  const settingItems: WorkShiftSettingType[] = result?.items || [];

  return settingItems.map((item) => ({
    ...item,
    holiday: item.holiday.map((acc) => ({
      ...acc,
      active: true,
      end: moment(`${item.ad_year}-${item.month}-${acc.day}`),
      resource: acc.name || "",
      start: moment(`${item.ad_year}-${item.month}-${acc.day}`),
      title: HOLIDAY_TAG,
    })),
  }));
};

const GetEmployeeForWorkShifts: Handler<
  AddEmployeeParamsType,
  Promise<EmployeeSerializer[]>
> = async (controller, params) => {
  const state = controller.getState();

  const workShiftSettings = state.SetupWorkScheduleSequence?.workShiftSettings ?? [];
  // Employee mode
  const employees: EmployeeSerializer[] = params.employeeData ? [params.employeeData as any] : [];

  const workShiftSetting = workShiftSettings.find(
    (item) =>
      item.ad_year === params.viewMonth.year() && item.month === params.viewMonth.month() + 1
  );
  const employeeIds = new Set(
    (workShiftSetting?.employees ?? []).map((employee) => employee.employee)
  );

  // Department mode
  if (params.departmentId) {
    const [result] = await EmployeeListAPI.list({
      apiToken: controller.apiToken,
      params: { affiliate: params.departmentId, limit: 99999 },
    });

    employees.push(...(result?.items ?? []));
  }

  return employees.filter((employee) => !employeeIds.has(Number(employee.id)));
};

/** ====================================================  */
/**                         Utils                         */
/** ====================================================  */
const applyBlankToOff = (data: {
  employee: number;
  events: EventsTimeShiftType[];
  listDaysOfMonth: Moment[];
  type: ActionSaveType;
}): EventsTimeShiftType[] => {
  const { employee, events, listDaysOfMonth, type } = data;

  if (type === "NO_BLANK_OFF") {
    return events;
  }

  return listDaysOfMonth.flatMap((day) => {
    const dayEvents = events.filter((event) => moment(event.start).isSame(day, "day"));

    return dayEvents.length > 0
      ? dayEvents
      : [{ employee, end: day, shift_status: "O", start: day, title: "" }];
  });
};

export const parseMonthTimeENAndTH = (viewMonth: Moment) =>
  `${viewMonth.format("DD MMMM YYYY")} (${viewMonth.clone().locale("TH").format("DD MMMM")} ${
    Number(viewMonth.format("YYYY")) + 543
  })`;

export const parseDateTimeENAndTH = (viewMonth: Moment) =>
  `${viewMonth.format("MMMM YYYY")} (${viewMonth.clone().locale("TH").format("MMMM")} ${
    Number(viewMonth.format("YYYY")) + 543
  })`;

export const adjustForOvernight = (start: moment.Moment, end: moment.Moment) =>
  end.isBefore(start) ? end.clone().add(1, "days") : end;

export const calculateTotalHours = (
  start?: moment.Moment | string | null,
  end?: moment.Moment | string | null
): number => {
  if (!start || !end) {
    return 0;
  }

  // แปลง string เป็น moment object ถ้าจำเป็น
  const startMoment = typeof start === "string" ? moment(start, "HH:mm") : start;
  const endMoment = typeof end === "string" ? moment(end, "HH:mm") : end;

  const adjustedEnd = adjustForOvernight(startMoment, endMoment);
  const totalHours = adjustedEnd.diff(startMoment, "hours", true);

  return Number(totalHours.toFixed(2));
};

const calculateLeaveHours = (leaveEvents: EventsTimeShiftType[]): number => {
  let totalLeaveHours = 0;

  for (const event of leaveEvents) {
    // ถ้ามี leave_option ให้ใช้การคำนวณแบบ fixed hours
    if (["AH", "H"].includes(event.leave_option || "")) {
      totalLeaveHours += 4; // ครึ่งวัน = 4 ชั่วโมง
    } else if (["AF", "F"].includes(event.leave_option || "")) {
      totalLeaveHours += 8; // เต็มวัน = 8 ชั่วโมง
    } else {
      // ถ้าไม่มี leave_option หรือไม่ใช่ H/F ให้คำนวณจาก start-end เหมือนเดิม
      totalLeaveHours += calculateTotalHours(event.start, event.end);
    }
  }

  return totalLeaveHours;
};

// calculateWorkingHours - คำนวณชั่วโมงตามตารางกะเวลา (Total work hour)
export const calculateWorkingHours = (events: EventsTimeShiftType[], day: moment.Moment) => {
  const filteredEvents = events.filter((event) => event.start.isSame(day, "day"));

  // Work events: เหตุการณ์ที่ไม่ใช่การลา และสถานะไม่ใช่ OFF
  const workEvents = filteredEvents.filter(
    (event) => !event.leave_option && event.shift_status !== "O"
  );
  // Leave events: เหตุการณ์ลาที่ได้รับอนุมัติแล้ว (A = Approved)
  const leaveEvents = filteredEvents.filter(
    (event) => event.leave_option && event.approval_status === "A"
  );
  // All leave events: เหตุการณ์ลาทั้งหมด ไม่ว่าจะอนุมัติหรือไม่
  const allLeaveEvents = filteredEvents.filter((event) => event.leave_option);
  // Leave events ที่มีการผูกกับ shift time
  const leaveWithShiftEvents = leaveEvents.flatMap((event) => event.work_shift ?? []);
  // ขยาย leave events ให้รวม work_shift หรือใช้ event เดิมถ้าไม่มี work_shift
  const expandedLeaveEvents = leaveEvents.flatMap((event): EventsTimeShiftType[] =>
    Array.isArray(event.work_shift)
      ? (event.work_shift as EventsTimeShiftType[])
      : [event as EventsTimeShiftType]
  );

  // คำนวณชั่วโมงทำงาน (เฉพาะ work events)
  const eventHours = workEvents.map((event) => {
    const workingHours = calculateTotalHours(event.start, event.end);
    const breakDuration = calculateBreakTime(event.start, event.end, event);

    return Math.max(0, workingHours - breakDuration);
  });

  const leaveHours = calculateLeaveHours(expandedLeaveEvents);
  const allLeaveHours = calculateLeaveHours(allLeaveEvents);
  const leaveWithShiftHours = calculateLeaveHours(leaveWithShiftEvents);

  let totalHours = 0;
  let breakLeaveOverlapHours = 0;

  for (const hour of eventHours) {
    totalHours += hour;
  }

  // หา work events ที่มีการลาซ้อนทับ และคำนวณเวลาที่ซ้อนทับ
  if (leaveWithShiftEvents.length > 0) {
    breakLeaveOverlapHours = leaveWithShiftEvents.reduce(
      (total, event) => total + calculateBreakLeaveOverlap(event),
      0
    );
  }

  return {
    // เวลาที่ซ้อนทับระหว่างการพักและการลา
    breakLeaveOverlapHours,
    eventHours,
    filteredEvents,
    // ตรวจสอบว่ามีรายการที่ได้รับอนุมัติหรือไม่ (leave หรือ work event ที่อนุมัติ)
    hasApproved:
      leaveEvents.length > 0 || workEvents.some((event) => event.approval_status === "A"),
    // กรณีเป็นเวร OFF แสดง OFF
    isApprovedOff: filteredEvents.some(
      (item) => item.shift_status === "O" && item.approval_status === "A"
    ),
    // Total work hour : ระยะเวลาของ work time
    totalHours: Number(totalHours.toFixed(2)),
    // Leave hour : ชั่วโมงลาที่ได้รับอนุมัติ
    leaveHours: Number((leaveHours - breakLeaveOverlapHours).toFixed(2)),
    // All leave hour : ชั่วโมงลาทั้งหมด ไม่ filter approve (สีขาว)
    allLeaveHours: Number(allLeaveHours.toFixed(2)),
    // Leave hour : ชั่วโมงลาที่ได้รับอนุมัติและผูก shift time
    leaveWithShiftHours,
  };
};

// hasHistory - ตรวจสอบว่ามี work events ที่มี parent หรือไม่
export const hasHistoryForDay = (logs: EventsTimeShiftType[], day: moment.Moment): boolean => {
  const filteredEvents = logs.filter((log) => log.start.isSame(day, "day"));
  const workEvents = filteredEvents.filter(
    (event) => !event.leave_option && event.shift_status !== "O"
  );

  return workEvents.some((event) => event.id);
};

// คำนวณการปรับเวลา
const roundUpToHalfHour = (diffMinutes: number) => {
  const halfHour = 30;

  if (diffMinutes < halfHour) {
    return halfHour;
  }

  // มากกว่าหรือเท่ากับ 30 ปัดขึ้นไปครึ่งชั่วโมงถัดไป
  const roundedHalfHours = Math.floor(diffMinutes / halfHour) + 1;

  return roundedHalfHours * halfHour;
};

const getEffectiveTimes = (
  clockIn: moment.Moment | string,
  clockOut: moment.Moment | string,
  shift: ShiftType
) => {
  const adjustedShiftEnd = adjustForOvernight(moment(shift.start), moment(shift.end));
  const clockInMoment = moment(clockIn);
  const clockOutMoment = moment(clockOut);
  const shiftStartMoment = moment(shift.start);

  // กำหนดเวลาเริ่มต้นที่มีผล (effectiveStart)
  let effectiveStart = shiftStartMoment;

  if (clockInMoment.isAfter(shiftStartMoment)) {
    const lateMinutes = clockInMoment.diff(shiftStartMoment, "minutes");
    const roundedMinutes = roundUpToHalfHour(lateMinutes);

    effectiveStart = shiftStartMoment.clone().add(roundedMinutes, "minutes");
  }

  // กำหนดเวลาสิ้นสุดที่มีผล (effectiveEnd)
  let effectiveEnd = adjustedShiftEnd;

  if (clockOutMoment.isBefore(adjustedShiftEnd)) {
    const earlyMinutes = adjustedShiftEnd.diff(clockOutMoment, "minutes");
    const roundedMinutes = roundUpToHalfHour(earlyMinutes);

    effectiveEnd = adjustedShiftEnd.clone().subtract(roundedMinutes, "minutes");
  }

  return { effectiveEnd, effectiveStart };
};

export const calculateBreakTime = (
  effectiveStart: moment.Moment,
  effectiveEnd: moment.Moment,
  shift: ShiftType
): number => {
  if (!shift.end_break_time || !shift.start_break_time || !shift.is_break_time) {
    return 0;
  }

  const breakStart = moment(shift.start_break_time, ["HH:mm:ss", "HH:mm"]);
  const breakEnd = moment(shift.end_break_time, ["HH:mm:ss", "HH:mm"]);

  if (!breakStart.isValid() || !breakEnd.isValid()) {
    return 0;
  }

  // จัดการกรณี effectiveEnd ข้ามวัน
  const adjustedEffectiveEnd = adjustForOvernight(effectiveStart, effectiveEnd);

  const shiftStart = moment(shift.start);
  const shiftEnd = moment(shift.end);
  const isOvernightShift = shiftEnd.isBefore(shiftStart);

  // ตั้ง break time ให้เป็นวันเดียวกับ shift start
  const breakStartTime = shiftStart
    .clone()
    .hour(breakStart.hour())
    .minute(breakStart.minute())
    .second(breakStart.second());
  const breakEndTime = shiftStart
    .clone()
    .hour(breakEnd.hour())
    .minute(breakEnd.minute())
    .second(breakEnd.second());

  // Night shift: ถ้า break time น้อยกว่า shift start แสดงว่าอยู่วันถัดไป
  if (isOvernightShift) {
    if (breakStartTime.hour() < shiftStart.hour()) {
      breakStartTime.add(1, "day");
    }

    if (breakEndTime.hour() < shiftStart.hour()) {
      breakEndTime.add(1, "day");
    }
  }

  // ตรวจสอบว่าช่วงเวลาทำงานทับกับเวลาพัก
  if (effectiveStart.isBefore(breakEndTime) && adjustedEffectiveEnd.isAfter(breakStartTime)) {
    return calculateTotalHours(breakStartTime, breakEndTime);
  }

  return 0;
};

// คำนวณเวลาที่ break time ทับกับ leave time
const calculateBreakLeaveOverlap = (shift: ShiftType): number => {
  if (
    !shift.is_break_time ||
    !shift.start_break_time ||
    !shift.end_break_time ||
    !shift.leave_start_time ||
    !shift.leave_end_time
  ) {
    return 0;
  }

  const startTime = shift.start.format("YYYY-MM-DD");
  const leaveStart = moment(`${startTime} ${shift.leave_start_time}`);
  const leaveEnd = moment(`${startTime} ${shift.leave_end_time}`);

  return calculateBreakTime(leaveStart, leaveEnd, shift);
};

// คำนวณชั่วโมงสุทธิ (หัก break time แล้ว)
export const calculateNetWorkingHours = (
  startTime?: Moment | string | null,
  endTime?: Moment | string | null,
  shift?: Pick<ShiftType, "end_break_time" | "is_break_time" | "start_break_time">
) => {
  if (!startTime || !endTime || !shift) {
    return 0;
  }

  let startDate = moment(startTime);
  let endDate = moment(endTime);

  if (typeof startTime === "string" && typeof endTime === "string") {
    const currentDate = moment().format("YYYY-MM-DD");

    startDate = moment(`${currentDate} ${startTime}`);
    endDate = moment(`${currentDate} ${endTime}`);
  }

  const breakHours = calculateBreakTime(startDate, endDate, {
    ...shift,
    end: endDate,
    start: startDate,
  });
  const totalHours = calculateTotalHours(startDate, endDate);
  const workingHours = totalHours - breakHours;

  return Number(workingHours.toFixed(2));
};

const isTimeRangeOverlapping = (
  start1: moment.Moment,
  end1: moment.Moment,
  start2: moment.Moment,
  end2: moment.Moment
) => {
  const adjustedEnd1 = adjustForOvernight(start1, end1);
  const adjustedEnd2 = adjustForOvernight(start2, end2);

  return start1.isBefore(adjustedEnd2) && adjustedEnd1.isAfter(start2);
};

// calculateAttendance - คำนวณข้อมูล attendance ทั้งหมด
export const calculateAttendance = ({
  clockIn,
  clockOut,
  scheduledHours,
  shift,
}: AttendanceParams): AttendanceResult => {
  const result = {
    actualWorkHours: 0,
    breakLeaveOverlapHours: 0,
    earlyOutMinutes: 0,
    lateMinutes: 0,
    overtimeHours: 0,
  };

  let isOutsideShift = false;

  // Late: สาย >= 1 นาที (ปัดเศษวินาทีลง)
  if (clockIn) {
    const diffMs = moment(clockIn).diff(moment(shift.start));
    const lateMinutes = Math.floor(diffMs / 60000);

    if (lateMinutes >= 1) {
      result.lateMinutes = lateMinutes;
    }
  }

  // Ceiling seconds: ออกก่อนเวลา (ปัดเศษวินาทีขึ้น)
  if (clockOut) {
    const adjustedShiftEnd = adjustForOvernight(moment(shift.start), moment(shift.end));
    const diffMs = adjustedShiftEnd.diff(moment(clockOut));

    if (diffMs > 0) {
      result.earlyOutMinutes = Math.ceil(diffMs / 60000);
    }
  }

  // Actual work hour: คำนวณเวลาทำงานจริง
  if (clockIn && clockOut) {
    // ตรวจสอบว่า clock time อยู่ในช่วงกะหรือไม่
    const isOverlap = isTimeRangeOverlapping(
      moment(clockIn),
      moment(clockOut),
      moment(shift.start),
      moment(shift.end)
    );

    isOutsideShift = !isOverlap;

    if (isOverlap) {
      // เมื่ออยู่ในกะแล้ว ค่อยหา effective time
      const { effectiveEnd, effectiveStart } = getEffectiveTimes(clockIn, clockOut, shift);

      // คำนวณชั่วโมงทำงาน
      let hours = calculateTotalHours(effectiveStart, effectiveEnd);

      hours -= calculateBreakTime(effectiveStart, effectiveEnd, shift);

      // คำนวณ actual hours และ overtime (ปัดเศษนาทีลง)
      const actualHoursBeforeCap = Number(Math.max(0, hours).toFixed(2));

      result.actualWorkHours = Math.min(actualHoursBeforeCap, scheduledHours);
    }

    result.overtimeHours =
      calculateNetWorkingHours(shift.OT_clock_in_1hr, shift.OT_clock_out_1hr, shift) +
      calculateNetWorkingHours(shift.OT_clock_in_1_5hr, shift.OT_clock_out_1_5hr, shift) +
      calculateNetWorkingHours(shift.OT_clock_in_3hr, shift.OT_clock_out_3hr, shift);
  }

  result.breakLeaveOverlapHours = calculateBreakLeaveOverlap(shift);

  // รีเซ็ต การมาสาย/ออกก่อนเวลา หาก clock time อยู่นอกช่วงกะ
  if (isOutsideShift) {
    result.lateMinutes = 0;
    result.earlyOutMinutes = 0;
  }

  return {
    actualWorkHour: result.actualWorkHours > 0 ? `${result.actualWorkHours} hr` : "-",
    actualWorkHours: result.actualWorkHours,
    breakLeaveOverlapHours: result.breakLeaveOverlapHours,
    earlyOutMin: result.earlyOutMinutes > 0 ? `${result.earlyOutMinutes} min` : "-",
    earlyOutMinutes: result.earlyOutMinutes,
    isEarlyOut: result.earlyOutMinutes > 0,
    isLate: result.lateMinutes > 0,
    isOutsideShift,
    lateMin: result.lateMinutes > 0 ? `${result.lateMinutes} min` : "-",
    lateMinutes: result.lateMinutes,
    overtimeHours: result.overtimeHours,
  };
};

// calculateTotalAttendance - คำนวณชั่วโมงทำงานรวมทั้งวันจากหลาย events
export const calculateTotalAttendance = (events: EventsTimeShiftType[], day: moment.Moment) => {
  const result = {
    actualWorkHours: 0,
    hasApproved: false,
    overtimeHours: 0,
  };

  const filteredEvents = events.filter((event) => event.start.isSame(day, "day"));
  const approvedEvents = filteredEvents.filter((event) => event.approval_status === "A");
  const approvedWorkEvents = approvedEvents.filter(
    (event) => !event.leave_option && event.shift_status !== "X"
  );
  const workEventIds = new Set(approvedWorkEvents.map((event) => event.id));
  const allWorkShift = approvedEvents.flatMap((item) => item.work_shift ?? []);

  const filteredEventsWithShifts = approvedEvents.map((event) => {
    const matchingShift = event.work_shift?.find((ws) => workEventIds.has(ws.id));
    const approvedShiftData = approvedWorkEvents.find((item) => item.id === matchingShift?.id);

    // Filter shift status="L" ออกเนื่องจากลาเต็ม Work shift ไม่ต้องลบวันลาออก
    return event.work_shift && matchingShift
      ? { ...event, work_shift: approvedShiftData?.shift_status === "L" ? [] : [matchingShift] }
      : { ...event, work_shift: undefined };
  });

  const workingHours = calculateWorkingHours(filteredEventsWithShifts, day);

  // Set approval status if we approve any working hours
  result.hasApproved = approvedEvents.some((event) => event.shift_status !== "O");

  // Calculate actual work hours and overtime for approved work events
  for (const event of filteredEventsWithShifts) {
    const leaveTime = allWorkShift.find((item) => event.id === item.id);

    let clockIn = event.clock_in_datetime;

    // ถ้ามีการลาให้เอาชั่วโมงลามาลบ  Clock in เพื่อให้เวลาทำงานไม่โดนลบวันลา
    if (clockIn && leaveTime) {
      const deductionHours = calculateTotalHours(leaveTime.start, leaveTime.end);

      clockIn = moment(clockIn).add(-deductionHours, "hours").toISOString();
    }

    const attendance = calculateAttendance({
      clockIn,
      clockOut: event.clock_out_datetime,
      scheduledHours: workingHours.totalHours,
      shift: event,
    });

    result.actualWorkHours += attendance.actualWorkHours;
    result.overtimeHours += attendance.overtimeHours;
  }

  result.actualWorkHours -= workingHours.leaveWithShiftHours - workingHours.breakLeaveOverlapHours;

  result.overtimeHours = Number(result.overtimeHours.toFixed(2));

  // Check if all absence events have approval status
  const absenceEvents = filteredEvents.filter(
    (event) => !(event.leave_option && !event.work_shift)
  );
  const isAllAbsent =
    absenceEvents.length > 0 &&
    absenceEvents.every((event) => event.approval_status === "A" && event.shift_status === "X");

  return {
    ...result,
    actualWorkHours: Number(Math.max(result.actualWorkHours, 0).toFixed(2)),
    filteredEvents,
    isAllAbsent,
  };
};

export const calculateShiftTimeSummary = (events: EventsTimeShiftType[]) => {
  const result = {
    approve: {
      actualWorkHours: 0,
      leaveHours: 0,
      OTclock1_5hr: 0,
      OTclock1hr: 0,
      OTclock3hr: 0,
      totalDayOff: 0,
    },
    leaveHours: 0,
    totalDayOff: 0,
  };

  // Group events by day
  const eventsByDay: Record<string, EventsTimeShiftType[]> = {};

  for (const event of events) {
    const dayKey = event.start.format("YYYY-MM-DD");

    eventsByDay[dayKey] ??= [];
    eventsByDay[dayKey].push(event);
  }

  for (const [dayKey, dailyEvents] of Object.entries(eventsByDay)) {
    const day = moment(dayKey);

    const workingHours = calculateWorkingHours(dailyEvents, day);

    result.totalDayOff += dailyEvents.filter((event) => event.shift_status === "O").length;
    result.leaveHours += workingHours.allLeaveHours;

    // Use calculateTotalAttendance for actualWorkHours
    const attendance = calculateTotalAttendance(events, day);

    result.approve.actualWorkHours += attendance.actualWorkHours;

    // Calculate other approved values
    const approvedEvents = dailyEvents.filter((event) => event.approval_status === "A");

    result.approve.totalDayOff += approvedEvents.filter(
      (event) => event.shift_status === "O"
    ).length;

    result.approve.leaveHours += workingHours.leaveHours;

    // Calculate overtime
    for (const event of approvedEvents) {
      result.approve.OTclock1hr += calculateNetWorkingHours(
        event.OT_clock_in_1hr,
        event.OT_clock_out_1hr,
        event
      );

      result.approve.OTclock1_5hr += calculateNetWorkingHours(
        event.OT_clock_in_1_5hr,
        event.OT_clock_out_1_5hr,
        event
      );

      result.approve.OTclock3hr += calculateNetWorkingHours(
        event.OT_clock_in_3hr,
        event.OT_clock_out_3hr,
        event
      );
    }
  }

  result.approve.OTclock1hr = Number(result.approve.OTclock1hr.toFixed(2));
  result.approve.OTclock1_5hr = Number(result.approve.OTclock1_5hr.toFixed(2));
  result.approve.OTclock3hr = Number(result.approve.OTclock3hr.toFixed(2));

  return { ...result };
};

// ตรวจสอบและหา fields ที่ต้อง clear ค่า
export const getFieldsToClear = <T extends TimeFieldsData>(config: ValidationConfig<T>) => {
  const { date, endTime, fields, startTime, times } = config;

  if (!startTime || !endTime) {
    return {};
  }

  let shiftStart: moment.Moment;
  let shiftEnd: moment.Moment;

  // Handle different input types
  if (typeof startTime === "string" && typeof endTime === "string" && date) {
    shiftStart = moment(`${date} ${startTime}`);

    shiftEnd = adjustForOvernight(shiftStart, moment(`${date} ${endTime}`));
  } else {
    shiftStart = moment(startTime);
    shiftEnd = moment(endTime);
  }

  const fieldsToClear: Record<string, string> = {};

  // วนลูปตรวจสอบแต่ละ field
  for (const field of fields) {
    const fieldStartTime = times[field.start as keyof T];
    const fieldEndTime = times[field.end as keyof T];

    // หากเวลาไม่อยู่ในช่วงเวลาทำงาน ให้เพิ่มเข้าในรายการที่ต้อง Clear ค่า
    if (!isTimeWithinShift(shiftStart, shiftEnd, fieldStartTime, fieldEndTime)) {
      fieldsToClear[field.end] = "";
    }
  }

  return fieldsToClear;
};

// Format เวลาถ้าอยู่ในช่วงที่ถูกต้อง
export const formatTimeIfValid = (config: FormatTimeConfig): string => {
  const { date, endTime, isOutsideRange = false, startTime, timeValue } = config;

  // ถ้าไม่มีเวลา start/end หรือไม่มี value ให้ return ค่าว่าง
  if (!startTime || !endTime || !timeValue || isOutsideRange) {
    return "";
  }

  let shiftStart: moment.Moment;
  let shiftEnd: moment.Moment;

  // Handle different input types
  if (date) {
    shiftStart = moment(`${date} ${startTime}`);

    shiftEnd = adjustForOvernight(shiftStart, moment(`${date} ${endTime}`));
  } else {
    shiftStart = moment(startTime);
    shiftEnd = moment(endTime);
  }

  const inputTime = moment(`${shiftEnd.format("YYYY-MM-DD")} ${timeValue}`);

  // ตรวจสอบว่า input time อยู่ในช่วง start-end หรือไม่
  // ถ้าไม่อยู่ในช่วง ให้ลองใช้วันที่ของ start แทน
  const adjustedTime = inputTime.isBetween(shiftStart, shiftEnd, null, "[]")
    ? inputTime
    : moment(`${shiftStart.format("YYYY-MM-DD")} ${timeValue}`);

  // return value ถ้าเวลาที่ปรับแล้วอยู่ในช่วงที่ถูกต้อง
  return adjustedTime.isBetween(shiftStart, shiftEnd, null, "[]") ? timeValue : "";
};

const formatWorkShiftEvent = (
  event: WorkShiftResponseType,
  viewMonth: Moment
): EventsTimeShiftType => {
  const template = event.shift_status === "O" ? null : event.template_detail;
  const startTime =
    event.template_detail?.start_time || event.manual_template_start_time || "00:00";
  const endTime = event.template_detail?.end_time || event.manual_template_end_time || "23:59";
  const year = `${viewMonth.format("YYYY-MM")}-${event.day}`;

  return {
    id: event.id,
    approval_status: event.approval_status,
    clock_in: event.clock_in,
    clock_in_datetime: event.clock_in_datetime,
    clock_out: event.clock_out,
    clock_out_datetime: event.clock_out_datetime,
    color_code: event.manual_template_color_code,
    employee: Number(event.employee_id),
    end: moment(`${year} ${endTime}`),
    end_break_time: event.manual_template_end_break_time,
    is_break_time: event.manual_template_is_break_time,
    leave_end_time: event.leave_end_time,
    leave_start_time: event.leave_start_time,
    OT_clock_in_1_5hr: event.OT_clock_in_1_5hr,
    OT_clock_in_1hr: event.OT_clock_in_1hr,
    OT_clock_in_3hr: event.OT_clock_in_3hr,
    OT_clock_out_1_5hr: event.OT_clock_out_1_5hr,
    OT_clock_out_1hr: event.OT_clock_out_1hr,
    OT_clock_out_3hr: event.OT_clock_out_3hr,
    parent: event.parent,
    shift_status: event.shift_status,
    shift_type: event.manual_template_shift_type,
    start: moment(`${year} ${startTime}`),
    start_break_time: event.manual_template_start_break_time,
    title: template?.name || "",
    ...(template && {
      code: template.code,
      color_code: template.color_code,
      end_break_time: template.end_break_time,
      is_break_time: template.is_break_time,
      shift_type: template.shift_type,
      start_break_time: template.start_break_time,
      template: template.id,
    }),
  };
};

const formatWorkShiftItems = (
  items: WorkShiftResponseType[],
  employees: WorkShiftSettingType["employees"],
  viewMonth: Moment
): WorkShiftType[] => {
  const groupedByEmployee: WorkShiftType[] = [];

  // Sort employees by code
  const sortedEmployees = [...employees].sort((a, b) =>
    a.employee_code.localeCompare(b.employee_code)
  );

  for (const employee of sortedEmployees) {
    const data: WorkShiftType = {
      id: employee.id,
      employee: employee.employee,
      employee_code: employee.employee_code,
      employee_full_name: employee.employee_full_name || "",
      events: [],
      logs: [],
      regular_work_hours: employee.regular_work_hours ?? null,
    };

    const filteredActiveItems = items.filter(
      (item) => Number(item.employee_id) === data.employee && !item.deleted
    );
    const filteredAllItems = items.filter((item) => Number(item.employee_id) === data.employee);

    data.events = filteredActiveItems.map((event) => formatWorkShiftEvent(event, viewMonth));
    data.logs = filteredAllItems.map((event) => formatWorkShiftEvent(event, viewMonth));

    groupedByEmployee.push(data);
  }

  return groupedByEmployee;
};

const sortWorkShiftList = (workShift: NonNullable<EventsTimeShiftType["work_shift"]>[number][]) =>
  [...workShift].sort((a, b) => {
    const startDiff = a.start.valueOf() - b.start.valueOf();

    if (startDiff !== 0) {
      return startDiff;
    }

    return a.end.valueOf() - b.end.valueOf();
  });

const mergeWorkShiftLeaveRequestItems = (
  items: WorkShiftType[],
  leaveRequest: LeaveRequestType[]
) =>
  items.map((item) => {
    const filtered = leaveRequest.filter((leave) => leave.employee_id === item.employee);

    if (filtered.length > 0) {
      const flattened = flattenLeaveRequestItems(filtered);
      const formattedItems = flattened.map((acc) => {
        const workShift = acc.work_shift_detail.work_shift;

        let status: ApproveStatusType | undefined;
        let leaveOption: EventsTimeShiftType["leave_option"] =
          acc.total_leave_days >= 1 ? "F" : "H";

        if (acc.status === "A") {
          status = "A";
        } else if (acc.status === "P") {
          status = "P";
        }

        const formattedWorkShift = (workShift ?? [])
          .map((ws) => ({
            ...ws,
            approval_status: status as ApproveStatusType,
            employee: item.employee,
            end: moment(`${ws.ad_year}-${ws.month}-${ws.day} ${ws.leave_end_time}`),
            leave_option: "A" as const,
            shift_status: "L" as const,
            start: moment(`${ws.ad_year}-${ws.month}-${ws.day} ${ws.leave_start_time}`),
            title: acc.name,
          }))
          .filter((ws) => ws.start.isSame(acc.start_date, "days"));

        if (status === "A") {
          leaveOption = `A${leaveOption}`;
        }

        const firstShiftTime = formattedWorkShift[0];
        const lastShiftTime = formattedWorkShift.slice(-1)[0];

        const data: EventsTimeShiftType = {
          approval_status: status,
          employee: item.employee,
          end:
            formattedWorkShift.length > 0
              ? moment(`${acc.end_date} ${lastShiftTime.leave_end_time}`)
              : moment(acc.end_date),
          leave_option: leaveOption,
          shift_status: "L",
          start:
            formattedWorkShift.length > 0
              ? moment(`${acc.start_date} ${firstShiftTime.leave_start_time}`)
              : moment(acc.start_date),
          title: acc.name,
          work_shift: formattedWorkShift.length > 0 ? sortWorkShiftList(formattedWorkShift) : null,
        };

        return data;
      });

      item.events.push(...formattedItems);
    }

    return item;
  });

const flattenLeaveRequestItems = (items: LeaveRequestType[]) => {
  const flattened: LeaveRequestType[] = [];

  for (const item of items) {
    const startDate = moment(item.start_date);
    const endDate = moment(item.end_date || item.start_date);

    const daysDiff = endDate.diff(startDate, "days") + 1;

    for (const [index] of Array.from({ length: daysDiff }).entries()) {
      const date = startDate.clone().add(index, "days").format("YYYY-MM-DD");

      flattened.push({ ...item, end_date: date, start_date: date });
    }
  }

  return flattened;
};

export const groupEventsByCodeOrTime = (events: EventsTimeShiftType[]) => {
  const templateGrouped: Record<string, { color_code: string; title: string; total: number }> = {};
  const manualGrouped: Record<string, { color_code: string; title: string; total: number }> = {};

  for (const event of events) {
    let groupKey = event.code || "";
    let title = `${groupKey} ${event.title}`;

    if (event.leave_option || event.shift_status === "O") {
      continue;
    }

    // เลือก grouped object ตาม event type (ไม่มี code = manual)
    const isManualEvent = !event.code;
    const targetGrouped = isManualEvent ? manualGrouped : templateGrouped;

    if (!event.code) {
      // ถ้าไม่มี code ให้ group by start+end+color
      const startTime = formatTime(event.start);
      const endTime = formatTime(event.end);

      groupKey = `${startTime}-${endTime}-${event.color_code}`;
      title = `${startTime}-${endTime}`;
    }

    targetGrouped[groupKey] ??= {
      color_code: event.color_code || "",
      title,
      total: 0,
    };

    targetGrouped[groupKey].total++;
  }

  // รวม template events ก่อน แล้วตามด้วย manual events
  return [...Object.values(templateGrouped), ...Object.values(manualGrouped)];
};

const formatTime = (time: Moment) => moment(time, "HH:mm").format("H:mm").replace(":00", "");

export const getDaysOfMonth = (viewMonth: Moment) => {
  const days: Moment[] = [];

  const currentMonth = viewMonth.clone().date(1);
  const daysInMonth = currentMonth.daysInMonth();

  for (let index = 0; index < daysInMonth; index++) {
    const d = currentMonth.clone().add(index, "days");

    days.push(d);
  }

  return days;
};
